import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Table,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FirstPage, LastPage } from "@mui/icons-material";
import { ReactNode } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { v4 as uuidv4 } from "uuid";
import { Pager } from "@floriday/floriday-ui";

export interface TableColumn<T, K extends keyof T = keyof T> {
  label: string;
  key: K;
  align?: "left" | "center" | "right";
  render?: (value: T[K], row: T) => ReactNode;
}

export interface GenericTableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowCount: number;
  footer?: ReactNode;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  selectedRowId?: string | number;
  getRowId?: (row: T) => string | number;
}

export function GenericTable<T>({
  columns,
  rows,
  rowCount,
  footer,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  onRowClick,
  emptyMessage,
  onPageChange,
  onPageSizeChange,
  selectedRowId,
  getRowId,
}: Readonly<GenericTableProps<T>>) {
  const search = useSearch({ strict: false });

  const page = parseInt(search.page ?? "1", 10);
  const pageSize = parseInt(search.size ?? String(defaultPageSize), 10);
  const totalPages = Math.ceil(rowCount / pageSize);

  const handleFirstPage = () => {
    if (page > 1) {
      onPageChange?.(1);
    }
  };

  const handleLastPage = () => {
    if (page < totalPages) {
      onPageChange?.(totalPages);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          flex: 1, 
          overflow: "auto",
          backgroundColor: "background.paper",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell 
                  key={String(col.key)} 
                  align={col.align ?? "left"}
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: 700,
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                    py: 1.5,
                  }}
                >
                  <Typography fontWeight={700}>{col.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 && emptyMessage ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const rowId = getRowId ? getRowId(row) : uuidv4();
                const isSelected =
                  selectedRowId !== undefined && rowId === selectedRowId;

                return (
                  <TableRow
                    key={String(rowId)}
                    hover
                    selected={isSelected}
                    sx={{
                      cursor: onRowClick ? "pointer" : "default",
                      backgroundColor: isSelected
                        ? (theme) => theme.palette.primary.main
                        : undefined,
                      "&.Mui-selected": {
                        backgroundColor: (theme) => theme.palette.primary.main,
                        "& .MuiTableCell-root": {
                          color: "#ffffff !important",
                        },
                        "& .MuiButton-root": {
                          color: "#ffffff !important",
                        },
                        "& a": {
                          color: "#ffffff !important",
                        },
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: (theme) => theme.palette.primary.dark,
                        "& .MuiTableCell-root": {
                          color: "#ffffff !important",
                        },
                        "& .MuiButton-root": {
                          color: "#ffffff !important",
                        },
                        "& a": {
                          color: "#ffffff !important",
                        },
                      },
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={String(col.key)}
                        align={col.align ?? "left"}
                        sx={{ py: 1.5 }}
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : ((row[col.key] as ReactNode) ?? "")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pager outside table for persistent visibility */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: "background.paper",
          px: 1.5,
          py: 0,
          mt: 0,
          mb: 0,
        }}
      >
        {footer}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <Pager
            currentPage={page}
            pageSize={pageSize}
            numberOfItems={rowCount}
            pageSizeOptions={pageSizeOptions}
            onChange={({ currentPage, pageSize: newPageSize }) => {
              if (newPageSize && newPageSize !== pageSize) {
                onPageSizeChange?.(newPageSize);
              }
              if (currentPage && currentPage !== page) {
                onPageChange?.(currentPage);
              }
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Jump to first page">
              <span>
                <IconButton
                  onClick={handleFirstPage}
                  disabled={page === 1}
                  size="small"
                >
                  <FirstPage />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Jump to last page">
              <span>
                <IconButton
                  onClick={handleLastPage}
                  disabled={page === totalPages || totalPages === 0}
                  size="small"
                >
                  <LastPage />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
